import { Component, Vue, Watch } from 'vue-property-decorator';
import UserService from '@/services/user.service';
import ParticipantService from '@/services/participant.service';
import LanguagePicker from '@/components/LanguagePicker/LanguagePicker';
import Loader from '@/components/Loader/Loader';
import MoreInfo from '@/components/MoreInfo/MoreInfo';
import Logo from '@/components/Logo/Logo';
import MobileMenu from '@/components/MobileMenu/MobileMenu';
import { WatchOptions } from 'vue';
import TokenService from '@/services/token.service';
import { McsisRole } from '@/models/constants/McsisRole';
import { APP_VERSION, getCorrespondenceLanguagesBySu, useParticipantPage } from '@/config/app.config';
import { loadLocaleByLanguageAndSuNameAsync } from '@/plugins/i18n';
import ModalTemplate from '@/components/ModalTemplate/ModalTemplate';
import Contact from '@/components/Contact/Contact';
import Stepper from '@/components/Stepper/Stepper';
import LocalStoreService from '@/store/local.store';
import { ActionTypes, GetterTypes, MutationTypes } from '@/store-vuex/types';
import moment from 'moment-timezone';
import { McsisConstants } from '@/models/constants/McsisConstants';

@Component({
  components: {
    LanguagePicker,
    Loader,
    MoreInfo,
    Logo,
    MobileMenu,
    ModalTemplate,
    Contact,
    Stepper,
  },
})
export default class App extends Vue {
  // Properties
  public fullYear = moment().format(McsisConstants.DefaultYearFormat);
  public mobileMenuOpen: boolean = false;
  public currentStepNumber: number = 0;
  public maxVisitedStepNumber: number = 0;
  public env = process.env.NODE_ENV;
  public languageChoices: boolean = true;
  private get suName() { return this.$route.params.screening; }
  private showUpdateModal: boolean = false;

  public get navigationSteps(): string[] {
    if (this.$route.params.screening !== undefined &&
      this.$route.params.screening !== null
    ) {
      if (useParticipantPage(this.$route.params.screening)) {
        // For canton Thurgau we can edit some participant data and integrate it through a dashboard in MC-SIS
        this.$store.dispatch(ActionTypes.setShowAppointmentParticipantStep, true);

        return [
          McsisConstants.AppointmentParticipantPage,
          McsisConstants.AppointmentDetailsPage,
          McsisConstants.AppointmentSelectionPage,
          McsisConstants.AppointmentRecapPage,
        ];
      }
    }

    this.$store.dispatch(ActionTypes.setShowAppointmentParticipantStep, false);

    // NOM de pages PROVISOIRES
    return [
      McsisConstants.AppointmentDetailsPage,
      McsisConstants.AppointmentSelectionPage,
      McsisConstants.AppointmentRecapPage,
    ];
  }

  /**
   * Function called when the component is initialized
   */
  public created() {
    // Init the application default timezone for the entire appointment web site
    moment.tz.setDefault(McsisConstants.DefaultTimeZone);
    this.checkAppVersion();
  }

  public mounted() {
    window.addEventListener('load', () => {
      // run after everything is in-place
      this.$store.commit(MutationTypes.setIsAppLoaded, true);
      this.setup();

      // Remove the locale from $i18n in order to force a "refresh" of the texts
      // by setting the former locale.
      const oldLocale = this.$i18n.locale;
      this.$i18n.locale = '';
      this.$i18n.locale = oldLocale;
    });
  }

  get isEdition(): boolean {
    return this.$store.getters[GetterTypes.hasRdv];
  }

  get identified(): boolean { return this.$store.getters[GetterTypes.isIdentified]; }

  get refreshTrys() {
    return parseInt(LocalStoreService.getItem(LocalStoreService.localItemKeys.appVersionTrys), 10) || 0;
  }
  set refreshTrys(value: number) {
    LocalStoreService.setItem(LocalStoreService.localItemKeys.appVersionTrys, value.toString());
  }

  /**
   * Check the app version when the App is first loaded
   * and force the refresh if the version is different
   * from the app-version.json file on the server.
   */
  public checkAppVersion(): void {
    fetch('/app-version.json')
      .then((res) => res.json())
      .then((data) => {
        this.$store.commit(MutationTypes.setAppVersion, {
          isLatest: data.version === APP_VERSION,
          lastCheckDate: moment().format(McsisConstants.DefaultDateFormat),
        });
        if (!this.$store.getters[GetterTypes.appVersion].isLatest) {
          if (this.refreshTrys > 1) {
            this.showUpdateModal = true;
          } else {
            this.refreshTrys++;
            window.location.reload();
          }
        } else {
          // Reset the values
          this.refreshTrys = 0;
        }
      })
      .catch((err) => {
        throw(err);
      });
  }

  public hideUpdateModal(): void {
    this.showUpdateModal = false;
  }

  /**
   * Setup the application on first loading
   */
  public async setup() {
    TokenService.setXsrfTokenInHeader();

    if (TokenService.tokenExists()) {
      const isTokenValid = await TokenService.isTokenValid()
      .then((res) => {
        return res;
      })
      .catch((err) => {
        return false;
      });
      if (isTokenValid) {
        let routePrefix = this.suName;
        if (!routePrefix) {
          // Get the route prefix from the connected participant/pro user
          routePrefix = await UserService.getUserRoutePrefix();
        }
        this.$isInRole(McsisRole.Participant)
          .then(async (isInRole: boolean) => {
            if (isInRole) {
              await ParticipantService.logout();
            }
          });
      } else {
        // In case a token exists but is no longer valid we force a logout from the
        // frontend to be sure to clean all data
        const routePrefix = this.suName;
        this.$isInRole(McsisRole.Participant)
          .then(async (isInRole: boolean) => {
            if (!isInRole) {
              ParticipantService.logoutFromFrontend();
              if (!!this.$route.name && this.$route.name !== 'login') {
                this.$router.push({
                  name: 'login',
                  params: {
                    screening: routePrefix,
                  },
                });
              }
            }
          });
      }
    }
  }

  public openingDossier(): void {
    // set maVisitedStepNumber to 0 in order to avoid stepping unchecked step
    this.maxVisitedStepNumber = 0;
  }

  public selectStep(step: number): void {
    if (this.$route.params.partId !== undefined && step <= this.maxVisitedStepNumber) {
      this.$router.push({
        name: this.navigationSteps[step - 1], // step is 1-based
        params: {
          screening: this.$route.params.screening,
          partId: this.$route.params.partId,
        },
      });
    }
  }

   /**
    * Update the current step number (by the selected step number sent from the grand-child component)
    * only if it is smaller than the maximum visited step (prevent from seeing step not already seen).
    */
    public onUpdateCurrentStepNumber(step: number): void {
      if (step <= this.maxVisitedStepNumber) {
        this.currentStepNumber = step;
        this.selectStep(step);
      }
    }

  // triggered from hamburger
  protected toggleMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }
  // triggered from mobile menu links
  protected closeMobMenu(): void {
    this.mobileMenuOpen = false;
  }

  protected setIdentified(status: boolean): void {
    this.$store.dispatch(ActionTypes.setIsIdentified, status);
  }
  // Get loading state from store
  protected get isLoading(): boolean { return this.$store.getters[GetterTypes.dataLoading]; }

  /**
   * Logs the participant or the user out of the app
   * @param routeName The route name where to be redirected after the logout
   * $router.go(0): reload page to reset store states
   */
  protected async logout(routeName: string|null): Promise<void> {
    if (this.identified) {
      await ParticipantService.logout().then( () => {
        this.$store.dispatch(ActionTypes.setInit);
        this.checkAppVersion();
      } );
    }

    if (routeName) {
      this.$router.push({ name: routeName });
    }
  }

  protected addPopupMessageBeforeUnload(): void {
    if (window.onbeforeunload) {
      return;
    }

    window.onbeforeunload = (ev: any) => {
      const e = ev || window.event;
      const warningText = this.$i18n.t(`msgs.notSavedDataWarningMessage`).toString();

      // For IE and Firefox
      if (e) {
        e.returnValue = warningText;
      }

      // For Safari, Chrome
      return warningText;
    };
  }

  protected removePopupMessageBeforeUnload(): void {
    if (window.onbeforeunload) {
      window.onbeforeunload = null;
    }
  }


  @Watch('$route.name', { immediate: true, deep: true } as WatchOptions)
  protected onRouteNameChanged(to: string, from: string) {
    if (from !== to && to !== undefined && this.navigationSteps.indexOf(to) !== -1) {
      this.currentStepNumber = this.navigationSteps.indexOf(to) + 1; // currentStepNumber is 1-based
      this.maxVisitedStepNumber = Math.max(this.currentStepNumber, this.maxVisitedStepNumber);
    }

    if (to !== undefined) {
      this.removePopupMessageBeforeUnload();
    }

    if (to === undefined) {
      this.removePopupMessageBeforeUnload();
    }
  }

  /**
   * Le su est récupéré depuis $route mais $route n'est pas forcément chargé avant
   * App.ts, c'est pour ça qu'il nous faut un watcher
   * @date 17.08.2023 - 11:26:21
   *
   * @protected
   * @param {string} suName - Nom du su récupéré via $route
   * @param {string} oldSuName - L'ancien su si il y a un changement d'url
   */
  @Watch('suName', { immediate: true, deep: true } as WatchOptions)
  protected async onLocaleChanged(suName: string, oldSuName: string) {
    if (oldSuName !== suName && suName !== undefined) {
      this.$store.dispatch(ActionTypes.setIsLanguagesLoaded, false);

      const supportedLanguages = getCorrespondenceLanguagesBySu(this.$route.params.screening);

      this.languageChoices = supportedLanguages.length !== 1;

      // Wait for ALL languages to finish loading (imports + merge)
      await Promise.all(
        supportedLanguages.map((lang: string) =>
          loadLocaleByLanguageAndSuNameAsync(lang, suName)
        )
      );

      this.$store.dispatch(ActionTypes.setIsLanguagesLoaded, true);
    }
  }
}
